import { useCallback, useMemo, useEffect } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import ImageUploadExtension from "../extensions/ImageUploadExtension";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Mention from "@tiptap/extension-mention";
import Bold from "@tiptap/extension-bold"; // 1. 导入Bold扩展

import LinkedContentNode from "../extensions/LinkedContentNode";
import ContentBlockNode from "../extensions/ContentBlockNode";
import InlineContentBlockNode from "../extensions/InlineContentBlockNode";
import { createContentBlockSuggestion } from "../extensions/ContentBlockSuggestion";
import { EDITOR_CONFIG } from "../utils/constants";

/**
 * 邮件编辑器Hook
 * 管理编辑器实例和相关操作
 */
export const useEmailEditor = (businessContext, initialContent, enableV2Mode, availableContentBlocks, handleEditorUpdate, contentBlockInstances, setContentBlockInstances, createContentBlockInstance, onAttachmentGenerated, updateInstanceFilter) => {
    // 创建内容块建议配置
    const contentBlockSuggestion = useMemo(() => {
        return createContentBlockSuggestion(availableContentBlocks);
    }, [availableContentBlocks]);

    // 创建编辑器实例
    const editor = useEditor(
        {
            extensions: [
                StarterKit.configure({
                    ...EDITOR_CONFIG.starterKit,
                    bold: false, // 2. 禁用StarterKit的默认bold
                }),
                // 3. 添加自定义配置的Bold扩展
                Bold.extend({
                    addAttributes() {
                        return {
                            ...this.parent?.(),
                            style: {
                                default: null,
                                parseHTML: (element) => element.getAttribute("style"),
                                renderHTML: (attributes) => {
                                    if (!attributes.style) {
                                        return {};
                                    }
                                    return { style: attributes.style };
                                },
                            },
                        };
                    },
                }).configure(),
                Underline,
                Link,
                ImageUploadExtension.configure({ inline: false }),
                // ⚠️ 内容块扩展必须在 TextStyle/Color 之前注册
                // TextStyle 会匹配所有带 style 属性的 <span>，如果先注册会抢先解析
                // 带 style 的内容块 <span>（如 CGO 模板），导致 InlineContentBlockNode 无法识别
                LinkedContentNode,
                ContentBlockNode,
                InlineContentBlockNode,
                TextStyle,
                Color,
                TextAlign.configure(EDITOR_CONFIG.textAlign),
                Table.configure(EDITOR_CONFIG.table),
                TableRow,
                TableHeader,
                TableCell,
                Mention.configure({
                    ...EDITOR_CONFIG.mention,
                    suggestion: contentBlockSuggestion,
                }),
            ],
            storage: {
                businessContext: businessContext,
                onAttachmentGenerated: onAttachmentGenerated,
                contentBlockInstances,
                updateInstanceFilter,
            },
            content: initialContent,
            editable: true,
            autofocus: true,
            onUpdate: ({ editor, transaction }) => {
                if (enableV2Mode) {
                    handleEditorUpdate(editor, transaction);
                }
            },
            onCreate: ({ editor }) => {
                // 编辑器创建完成后的初始化逻辑
            },
        },
        [contentBlockSuggestion, businessContext, initialContent, enableV2Mode, handleEditorUpdate],
    );

    // 动态更新编辑器storage中的回调函数
    useEffect(() => {
        if (editor && editor.storage) {
            editor.storage.onAttachmentGenerated = onAttachmentGenerated;
            editor.storage.businessContext = businessContext;
            editor.storage.contentBlockInstances = contentBlockInstances;
            editor.storage.updateInstanceFilter = updateInstanceFilter;
        }
    }, [editor, onAttachmentGenerated, businessContext, contentBlockInstances, updateInstanceFilter]);

    // V2.5 修改：插入内容块实例
    const insertContentBlock = useCallback(
        (blockId) => {
            if (!editor) return;

            // 创建新实例
            const newInstance = createContentBlockInstance(blockId);
            if (!newInstance) return;

            // 插入自定义节点，使用instanceId
            editor
                .chain()
                .focus()
                .insertContent({
                    type: "contentBlock",
                    attrs: {
                        instanceId: newInstance.instanceId,
                        contentBlockId: blockId,
                        contentBlockType: newInstance.type,
                        contentBlockLabel: newInstance.title,
                        dataKey: blockId,
                        status: "filled",
                    },
                })
                .run();

            // 更新实例状态为已插入
            setContentBlockInstances((prev) => prev.map((instance) => (instance.instanceId === newInstance.instanceId ? { ...instance, isInserted: true } : instance)));
        },
        [editor, createContentBlockInstance, setContentBlockInstances],
    );

    // V2.5 修改：插入行内内容块实例
    const insertInlineContentBlock = useCallback(
        (blockId) => {
            if (!editor) return;

            // 创建新实例
            const newInstance = createContentBlockInstance(blockId);
            if (!newInstance) return;

            // 插入行内自定义节点，使用instanceId
            editor
                .chain()
                .focus()
                .insertContent({
                    type: "inlineContentBlock",
                    attrs: {
                        instanceId: newInstance.instanceId,
                        contentBlockId: blockId,
                        contentBlockType: newInstance.type,
                        contentBlockLabel: newInstance.title,
                        dataKey: blockId,
                        status: "filled",
                        isInline: true,
                    },
                })
                .run();

            // 更新实例状态为已插入
            setContentBlockInstances((prev) => prev.map((instance) => (instance.instanceId === newInstance.instanceId ? { ...instance, isInserted: true } : instance)));
        },
        [editor, createContentBlockInstance, setContentBlockInstances],
    );

    // V2.5 修改：删除内容块实例
    const removeContentBlock = useCallback(
        (instanceId) => {
            if (!editor) return;

            // 查找并删除对应的节点
            const { state, dispatch } = editor.view;
            const { tr } = state;
            let found = false;

            state.doc.descendants((node, pos) => {
                if ((node.type.name === "contentBlock" || node.type.name === "inlineContentBlock") && node.attrs.instanceId === instanceId && !found) {
                    tr.delete(pos, pos + node.nodeSize);
                    found = true;
                }
            });

            if (found) {
                dispatch(tr);

                // 更新实例状态
                setContentBlockInstances((prev) => prev.map((instance) => (instance.instanceId === instanceId ? { ...instance, isInserted: false } : instance)));
            }
        },
        [editor, setContentBlockInstances],
    );

    // V2.5 修改：更新内容块实例
    const updateContentBlock = useCallback(
        (instanceId, params) => {
            if (!editor) return;

            // 查找并更新对应节点的属性
            const { state, dispatch } = editor.view;
            const { tr } = state;
            let found = false;

            state.doc.descendants((node, pos) => {
                if ((node.type.name === "contentBlock" || node.type.name === "inlineContentBlock") && node.attrs.instanceId === instanceId && !found) {
                    tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        ...params,
                    });
                    found = true;
                }
            });

            if (found) {
                dispatch(tr);

                // 更新实例状态
                setContentBlockInstances((prev) => prev.map((instance) => (instance.instanceId === instanceId ? { ...instance, ...params } : instance)));
            }
        },
        [editor, setContentBlockInstances],
    );

    return {
        editor,
        insertContentBlock,
        insertInlineContentBlock,
        removeContentBlock,
        updateContentBlock,
    };
};
